// Packages
import React, { memo, useRef } from 'react';
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    isSameDay,
    startOfMonth,
} from 'date-fns';
import { DayPicker, dateMatchModifiers, rangeIncludesDate } from 'react-day-picker';

// Kod mobilnog kalendara s više mjeseci renderiramo svaki mjesec zasebno
// kako promjena jednog mjeseca ne bi uzrokovala render svih ostalih.
const PROPS_EXCLUDED_FROM_STATIC_SIGNATURE = new Set([
    'modifiers',
    'formatters',
    'labels',
    'selected',
    'disabled',
    'hidden',
    'month',
    'numberOfMonths',
    'components',
    'locale',
]);

function serializeValue(value) {
    if (value instanceof Date) return String(value.getTime());
    if (value && typeof value === 'object') return JSON.stringify(value);

    return String(value);
}

function getStaticSignature(props) {
    const signature = Object.keys(props)
        .filter((key) => !PROPS_EXCLUDED_FROM_STATIC_SIGNATURE.has(key) && typeof props[key] !== 'function')
        .sort()
        .map((key) => `${key}=${serializeValue(props[key])}`)
        .join('&');

    return `${signature}&locale=${props.locale?.code ?? ''}`;
}

function getNodeSignature(node) {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(getNodeSignature).join('|');

    if (React.isValidElement(node)) {
        const { children, ...restProps } = node.props;
        const type = typeof node.type === 'string'
            ? node.type
            : (node.type.displayName || node.type.name || 'component');

        const propsSignature = Object.keys(restProps)
            .filter((key) => typeof restProps[key] !== 'function')
            .sort()
            .map((key) => `${key}=${serializeValue(restProps[key])}`)
            .join(',');

        return `<${type} ${propsSignature}>${getNodeSignature(children)}</${type}>`;
    }

    return String(node);
}

function getSelectionSignature(day, selected, mode) {
    if (!selected) return '';

    if (mode === 'range') {
        const { from, to } = selected;

        if (from && isSameDay(day, from)) return 'range_start;';
        if (to && isSameDay(day, to)) return 'range_end;';
        if (from && to && rangeIncludesDate({ from, to }, day, true)) return 'range_middle;';

        return '';
    }

    return dateMatchModifiers(day, selected) ? 'selected;' : '';
}

function getMonthSignature(monthDate, props) {
    const {
        modifiers = {},
        selected,
        mode,
        disabled,
        hidden,
        formatters,
    } = props;

    const modifierEntries = Object.entries(modifiers)
        .filter(([, matcher]) => matcher !== undefined);

    const formatDay = formatters?.formatDay;
    const days = eachDayOfInterval({
        start: monthDate,
        end: endOfMonth(monthDate),
    });

    return days.map((day) => {
        let signature = '';

        modifierEntries.forEach(([name, matcher]) => {
            if (dateMatchModifiers(day, matcher)) {
                signature += `${name};`;
            }
        });

        if (disabled && dateMatchModifiers(day, disabled)) {
            signature += 'disabled;';
        }

        if (hidden && dateMatchModifiers(day, hidden)) {
            signature += 'hidden;';
        }

        signature += getSelectionSignature(day, selected, mode);

        if (formatDay) {
            signature += getNodeSignature(formatDay(day));
        }

        return signature;
    }).join('\n');
}

// Handler mora ostati stabilan zbog memoiziranih mjeseci, ali uvijek poziva zadnji prop.
function useStableEventHandlers(props) {
    const latestPropsRef = useRef(props);
    latestPropsRef.current = props;

    const stableHandlersRef = useRef({});

    Object.keys(props).forEach((key) => {
        if (
            key.startsWith('on')
            && typeof props[key] === 'function'
            && !stableHandlersRef.current[key]
        ) {
            stableHandlersRef.current[key] = (...args) => (
                latestPropsRef.current[key]?.(...args)
            );
        }
    });

    return stableHandlersRef.current;
}

const MemoizedMonth = memo(
    ({ monthProps }) => React.createElement(DayPicker, monthProps),
    (prevProps, nextProps) => (
        prevProps.monthSignature === nextProps.monthSignature
        && prevProps.staticSignature === nextProps.staticSignature
    ),
);
MemoizedMonth.displayName = 'MemoizedMonth';

export default function DayPickerProxy(props) {
    const {
        month,
        numberOfMonths = 1,
        hideNavigation,
    } = props;

    const stableEventHandlers = useStableEventHandlers(props);

    const renderMonthsSeparately = hideNavigation
        && month
        && numberOfMonths > 1;

    if (!renderMonthsSeparately) {
        return React.createElement(DayPicker, props);
    }

    const firstMonth = startOfMonth(month);
    const staticSignature = getStaticSignature(props);

    const months = Array.from(
        { length: numberOfMonths },
        (_, index) => addMonths(firstMonth, index),
    );

    return (
        <div className="day-picker-proxy">
            {months.map((monthDate) => (
                <MemoizedMonth
                    key={monthDate.getTime()}
                    monthProps={{
                        ...props,
                        ...stableEventHandlers,
                        month: monthDate,
                        numberOfMonths: 1,
                    }}
                    monthSignature={getMonthSignature(monthDate, props)}
                    staticSignature={staticSignature}
                />
            ))}
        </div>
    );
}
